import { formatNumber } from '@/lib/format';
import { fieldOf, GROUP_SPECS, missingLabel, specOf } from './catalog';
import type { ComponentSpec } from './catalog';
import { estimateThrustPerMotor, missingEstimateInputs, NOMINAL_CELL_VOLTAGE } from './estimate';
import type { ComponentInstance, GroupId, MissingRef, Project } from './types';
import { fieldNumber, hasField, normalizedQty } from './values';
import { verdictFor } from './verdict';
import type { Verdict } from './verdict';

/** 电池可用放电比例：留 20% 余量，避免过放与电压跌落。 */
export const USABLE_BATTERY_RATIO = 0.8;

/**
 * 一个指标的通用外壳。
 *
 * `value === null` 表示「数据不足，算不出来」；`missing` 列出导致算不出来的参数，
 * 界面据此渲染「缺少参数“X”，未计入计算」；`notes` 是口径说明（用了估算值、回落数据源等）。
 */
export interface Metric<T> {
  value: T | null;
  missing: MissingRef[];
  notes: string[];
}

export interface MassSummary {
  totalG: number;
  payloadG: number;
  emptyG: number;
  byGroup: { group: GroupId; label: string; grams: number }[];
}

export type ThrustSource = 'input' | 'estimated' | 'mixed' | 'none';

export interface ThrustSummary {
  perMotorG: number | null;
  totalG: number;
  source: ThrustSource;
  /** 电机总数量（4 轴通常为 4，同轴双桨可为 8） */
  motorCount: number;
}

export interface TwrSummary {
  bare: number;
  effective: number;
  usableThrustG: number;
  reservePct: number;
}

export interface HoverSummary {
  throttlePct: number;
  reserveCapPct: number;
  headroomPct: number;
  feasible: boolean;
}

export interface PowerSummary {
  voltageV: number;
  maxCurrentA: number | null;
  maxPowerW: number | null;
  hoverCurrentA: number | null;
  hoverPowerW: number | null;
  /** 满油门电流的口径：实测电机电流，或回落到电调持续电流 */
  currentBasis: 'motor' | 'esc' | null;
  efficiencyGPerW: number | null;
}

export interface EnduranceSummary {
  minutes: number;
  capacityAh: number;
  usableRatio: number;
}

export interface CostSummary {
  total: number;
  per100g: number | null;
  byGroup: { group: GroupId; label: string; amount: number; share: number }[];
  lines: { label: string; amount: number; qty: number }[];
}

export type MarginStatus = 'ok' | 'warn' | 'fail' | 'unknown';

export interface MarginCheck {
  id: string;
  label: string;
  status: MarginStatus;
  /** 结论与依据，一句话说清 */
  detail: string;
}

export interface Assessment {
  reservePct: number;
  mass: Metric<MassSummary>;
  thrust: Metric<ThrustSummary>;
  twr: Metric<TwrSummary>;
  hover: Metric<HoverSummary>;
  power: Metric<PowerSummary>;
  endurance: Metric<EnduranceSummary>;
  cost: Metric<CostSummary>;
  margins: MarginCheck[];
  verdict: Verdict;
  /** 所有缺失参数去重后的汇总（按参数名聚合） */
  missingAll: MissingRef[];
  /** 已填写的数值字段数量，用于「数据完整度」 */
  providedFieldCount: number;
  /** 数据完整度：已填字段 / （已填字段 + 缺失参数） */
  completeness: number;
  /** 完全没有部件（空项目） */
  isEmpty: boolean;
}

/* ------------------------------------------------------------------ *
 * 内部工具
 * ------------------------------------------------------------------ */

interface NamedComponent {
  component: ComponentInstance;
  spec: ComponentSpec;
  /** 展示名：自定义名称，或「电机 2」这样的自动编号 */
  name: string;
  qty: number;
}

function nameComponents(project: Project): NamedComponent[] {
  const totals = new Map<string, number>();
  for (const component of project.components) {
    totals.set(component.kind, (totals.get(component.kind) ?? 0) + 1);
  }

  const seen = new Map<string, number>();
  return project.components.map((component) => {
    const spec = specOf(component.kind);
    const index = (seen.get(component.kind) ?? 0) + 1;
    seen.set(component.kind, index);
    const custom = component.label?.trim();
    const duplicated = (totals.get(component.kind) ?? 0) > 1;
    const name = custom && custom.length > 0 ? custom : duplicated ? `${spec.label} ${index}` : spec.label;
    return { component, spec, name, qty: normalizedQty(component) };
  });
}

/**
 * 缺失参数提示里使用的参数名前缀：
 * 载荷与自定义部件用实例名（「云台重量」），其余用部件类型名（「电机最大推力」）。
 */
function prefixFor(named: NamedComponent): string {
  if (named.spec.group === 'payload' || named.spec.kind === 'custom') return named.name;
  return named.spec.label;
}

function missingFor(named: NamedComponent, fieldKey: string): MissingRef | null {
  const field = fieldOf(named.spec.kind, fieldKey);
  if (!field) return null;
  const fieldLabel = missingLabel(prefixFor(named), field);
  return {
    componentId: named.component.id,
    componentLabel: named.name,
    fieldKey,
    fieldLabel,
    metricKey: fieldLabel,
  };
}

function pushMissing(list: MissingRef[], named: NamedComponent, fieldKey: string): void {
  const ref = missingFor(named, fieldKey);
  if (ref) list.push(ref);
}

/** 合并多组缺失项，按「部件 + 字段」去重 */
function mergeMissing(...groups: MissingRef[][]): MissingRef[] {
  const seen = new Set<string>();
  const merged: MissingRef[] = [];
  for (const group of groups) {
    for (const ref of group) {
      const key = `${ref.componentId}|${ref.fieldKey}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(ref);
    }
  }
  return merged;
}

/** 汇总去重：同一参数名只保留一条 */
function dedupeByLabel(refs: MissingRef[]): MissingRef[] {
  const seen = new Set<string>();
  const unique: MissingRef[] = [];
  for (const ref of refs) {
    if (seen.has(ref.metricKey)) continue;
    seen.add(ref.metricKey);
    unique.push(ref);
  }
  return unique;
}

const emptyMetric = <T>(): Metric<T> => ({ value: null, missing: [], notes: [] });

/* ------------------------------------------------------------------ *
 * 主流程
 * ------------------------------------------------------------------ */

export function assess(project: Project): Assessment {
  const named = nameComponents(project);
  const reservePct = normalizeReserve(project.reservePct);

  const mass = computeMass(named);
  const thrust = computeThrust(named);
  const twr = computeTwr(mass, thrust, reservePct);
  const hover = computeHover(twr);
  const power = computePower(named, mass, twr);
  const endurance = computeEndurance(named, power);
  const cost = computeCost(named, mass);

  const margins = computeMargins(named, { twr, hover, power });

  const missingAll = dedupeByLabel(
    mergeMissing(
      mass.missing,
      thrust.missing,
      twr.missing,
      hover.missing,
      power.missing,
      endurance.missing,
      cost.missing,
    ),
  );

  const providedFieldCount = countProvidedFields(named);
  const denominator = providedFieldCount + missingAll.length;

  return {
    reservePct,
    mass,
    thrust,
    twr,
    hover,
    power,
    endurance,
    cost,
    margins,
    verdict: verdictFor(twr.value?.effective ?? null),
    missingAll,
    providedFieldCount,
    completeness: denominator === 0 ? 0 : providedFieldCount / denominator,
    isEmpty: project.components.length === 0,
  };
}

function normalizeReserve(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 20;
  return Math.min(90, Math.max(0, value));
}

function countProvidedFields(named: NamedComponent[]): number {
  let count = 0;
  for (const item of named) {
    for (const field of item.spec.fields) {
      if (hasField(item.component, field.key)) count += 1;
    }
  }
  return count;
}

/* ---------------------------- 质量 ---------------------------- */

function computeMass(named: NamedComponent[]): Metric<MassSummary> {
  const missing: MissingRef[] = [];
  const notes: string[] = [];
  const byGroup = new Map<GroupId, number>();
  let total = 0;
  let payload = 0;
  let contributors = 0;

  for (const item of named) {
    const weight = fieldNumber(item.component, 'weight_g');
    if (weight === null) {
      pushMissing(missing, item, 'weight_g');
      continue;
    }
    const grams = weight * item.qty;
    total += grams;
    contributors += 1;
    byGroup.set(item.spec.group, (byGroup.get(item.spec.group) ?? 0) + grams);
    if (item.spec.group === 'payload') payload += grams;
  }

  const value: MassSummary | null =
    contributors === 0
      ? null
      : {
          totalG: total,
          payloadG: payload,
          emptyG: total - payload,
          byGroup: GROUP_SPECS.map((group) => ({
            group: group.id,
            label: group.label,
            grams: byGroup.get(group.id) ?? 0,
          })).filter((entry) => entry.grams > 0),
        };

  if (named.length === 0) {
    notes.push('尚未添加任何部件。');
  } else if (value && missing.length > 0) {
    notes.push('部分部件未填重量，总质量为已填部分之和。');
  }

  return { value, missing, notes };
}

/* ---------------------------- 推力 ---------------------------- */

interface EstimateContext {
  diameterIn: number | null;
  pitchIn: number | null;
  cells: number | null;
  blades: number | null;
  /** 数据来源部件名，用于口径说明 */
  sourceName: string | null;
}

function buildEstimateContext(named: NamedComponent[]): EstimateContext {
  const propeller = named.find(
    (item) => item.spec.kind === 'propeller' && fieldNumber(item.component, 'diameter_in') !== null,
  );
  const battery = named.find((item) => item.spec.kind === 'battery');

  return {
    diameterIn: propeller ? fieldNumber(propeller.component, 'diameter_in') : null,
    pitchIn: propeller ? fieldNumber(propeller.component, 'pitch_in') : null,
    blades: propeller ? fieldNumber(propeller.component, 'blades') : null,
    cells: battery ? fieldNumber(battery.component, 'cells') : null,
    sourceName: propeller?.name ?? null,
  };
}

function computeThrust(named: NamedComponent[]): Metric<ThrustSummary> {
  const missing: MissingRef[] = [];
  const notes: string[] = [];
  const motors = named.filter((item) => item.spec.kind === 'motor');

  if (motors.length === 0) {
    notes.push('尚未添加电机部件，无法计算推力。');
    return { ...emptyMetric<ThrustSummary>(), notes };
  }

  const context = buildEstimateContext(named);
  const estimateInputs = {
    diameterIn: context.diameterIn,
    pitchIn: context.pitchIn,
    cells: context.cells,
    blades: context.blades,
  };

  let totalThrust = 0;
  let motorCount = 0;
  let measuredCount = 0;
  let estimatedCount = 0;
  let blockedMotors = 0;
  const blockedMissing = new Set<string>();

  for (const motor of motors) {
    motorCount += motor.qty;
    const measured = fieldNumber(motor.component, 'thrust_g');
    if (measured !== null) {
      totalThrust += measured * motor.qty;
      measuredCount += motor.qty;
      continue;
    }

    const kv = fieldNumber(motor.component, 'kv');
    const inputs = { ...estimateInputs, kv };
    const estimated = estimateThrustPerMotor(inputs);
    if (estimated !== null) {
      totalThrust += estimated * motor.qty;
      estimatedCount += motor.qty;
      notes.push(
        `「${motor.name}」按经验估算 ${formatNumber(estimated, 0)} g/个（KV ${formatNumber(kv, 0)}${
          context.diameterIn !== null && context.pitchIn !== null
            ? ` · ${formatNumber(context.diameterIn, 1)}×${formatNumber(context.pitchIn, 1)} in`
            : ''
        }${context.cells !== null ? ` · ${formatNumber(context.cells, 0)}S` : ''}），误差约 ±20%。`,
      );
      continue;
    }

    pushMissing(missing, motor, 'thrust_g');
    blockedMotors += 1;
    for (const item of missingEstimateInputs(inputs)) blockedMissing.add(item);
  }

  if (blockedMotors > 0) {
    notes.push('部分电机未填最大推力，且经验估算所需的参数不齐，这些电机未计入推力。');
    if (blockedMissing.size > 0) {
      notes.push(`经验估算还需要：${[...blockedMissing].map((item) => `“${item}”`).join('、')}。`);
    }
  }

  const source: ThrustSource =
    measuredCount > 0 && estimatedCount > 0
      ? 'mixed'
      : measuredCount > 0
        ? 'input'
        : estimatedCount > 0
          ? 'estimated'
          : 'none';

  const value: ThrustSummary | null =
    motorCount === 0 || (measuredCount === 0 && estimatedCount === 0)
      ? null
      : {
          totalG: totalThrust,
          perMotorG: totalThrust / motorCount,
          source,
          motorCount,
        };

  if (source === 'estimated') {
    notes.unshift('全部推力均为经验估算值，请以实测数据为准。');
  }

  return { value, missing, notes };
}

/* ---------------------------- 推重比 ---------------------------- */

function computeTwr(
  mass: Metric<MassSummary>,
  thrust: Metric<ThrustSummary>,
  reservePct: number,
): Metric<TwrSummary> {
  const missing = mergeMissing(mass.missing, thrust.missing);
  const notes: string[] = [];

  const massG = mass.value?.totalG ?? null;
  const thrustG = thrust.value?.totalG ?? null;

  if (massG === null || thrustG === null || massG <= 0) {
    if (massG !== null && massG <= 0) notes.push('起飞质量必须大于 0。');
    return { value: null, missing, notes };
  }

  const usableThrustG = thrustG * (1 - reservePct / 100);
  const bare = thrustG / massG;
  const effective = usableThrustG / massG;

  return {
    value: { bare, effective, usableThrustG, reservePct },
    missing,
    notes,
  };
}

/* ---------------------------- 悬停油门 ---------------------------- */

/**
 * 悬停油门：螺旋桨推力近似与油门平方成正比，因此悬停油门 = √(1 / 推重比)。
 * 判据是它与「安全系数预留上限（100% − 预留）」的比较。
 */
function computeHover(twr: Metric<TwrSummary>): Metric<HoverSummary> {
  const summary = twr.value;
  if (!summary || summary.bare <= 0) {
    return { ...emptyMetric<HoverSummary>(), missing: twr.missing };
  }

  const throttlePct = Math.sqrt(1 / summary.bare) * 100;
  const reserveCapPct = 100 - summary.reservePct;

  return {
    value: {
      throttlePct,
      reserveCapPct,
      headroomPct: reserveCapPct - throttlePct,
      feasible: throttlePct <= reserveCapPct,
    },
    missing: twr.missing,
    notes: [],
  };
}

/* ---------------------------- 功率与电流 ---------------------------- */

function batteryVoltage(named: NamedComponent[]): { voltageV: number | null; named: NamedComponent | null } {
  const battery = named.find((item) => item.spec.kind === 'battery');
  if (!battery) return { voltageV: null, named: null };
  const cells = fieldNumber(battery.component, 'cells');
  if (cells === null || cells <= 0) return { voltageV: null, named: battery };
  return { voltageV: cells * NOMINAL_CELL_VOLTAGE, named: battery };
}

function weightedAverage(
  items: { value: number; weight: number }[],
): number | null {
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
  if (totalWeight <= 0) return null;
  const total = items.reduce((sum, item) => sum + item.value * item.weight, 0);
  return total / totalWeight;
}

function computePower(
  named: NamedComponent[],
  mass: Metric<MassSummary>,
  twr: Metric<TwrSummary>,
): Metric<PowerSummary> {
  const missing: MissingRef[] = [];
  const notes: string[] = [];

  const { voltageV, named: battery } = batteryVoltage(named);
  if (voltageV === null && battery) pushMissing(missing, battery, 'cells');

  const motors = named.filter((item) => item.spec.kind === 'motor');

  const motorCurrents = motors
    .map((item) => ({ value: fieldNumber(item.component, 'max_current_a'), qty: item.qty }))
    .filter((item): item is { value: number; qty: number } => item.value !== null);
  const escCurrents = named
    .filter((item) => item.spec.kind === 'esc')
    .map((item) => ({ value: fieldNumber(item.component, 'cont_current_a'), qty: item.qty }))
    .filter((item): item is { value: number; qty: number } => item.value !== null);

  let maxCurrentA: number | null = null;
  let currentBasis: PowerSummary['currentBasis'] = null;

  if (motorCurrents.length > 0) {
    const motorsWithCurrent = motors.filter((item) => fieldNumber(item.component, 'max_current_a') !== null);
    const totalQty = motors.reduce((sum, item) => sum + item.qty, 0);
    const coveredQty = motorsWithCurrent.reduce((sum, item) => sum + item.qty, 0);
    if (coveredQty < totalQty) {
      for (const motor of motors) {
        if (fieldNumber(motor.component, 'max_current_a') === null) pushMissing(missing, motor, 'max_current_a');
      }
      notes.push('部分电机未填最大电流，满油门电流为已填部分之和。');
    }
    maxCurrentA = motorCurrents.reduce((sum, item) => sum + item.value * item.qty, 0);
    currentBasis = 'motor';
  } else if (escCurrents.length > 0) {
    maxCurrentA = escCurrents.reduce((sum, item) => sum + item.value * item.qty, 0);
    currentBasis = 'esc';
    notes.push('电机最大电流缺失，满油门电流按电调持续电流估算（偏保守）。');
  } else if (motors.length > 0) {
    for (const motor of motors) pushMissing(missing, motor, 'max_current_a');
    notes.push('没有电机最大电流，也没有电调持续电流，无法估算电流与功率。');
  }

  const maxPowerW = voltageV !== null && maxCurrentA !== null ? voltageV * maxCurrentA : null;

  const efficiencies = motors
    .map((item) => ({ value: fieldNumber(item.component, 'efficiency_g_per_w'), weight: item.qty }))
    .filter((item): item is { value: number; weight: number } => item.value !== null);
  const efficiencyGPerW = weightedAverage(efficiencies);

  const massG = mass.value?.totalG ?? null;
  const bare = twr.value?.bare ?? null;

  let hoverPowerW: number | null = null;
  if (efficiencyGPerW !== null && efficiencyGPerW > 0 && massG !== null) {
    hoverPowerW = massG / efficiencyGPerW;
    notes.push(`悬停功率按电机效率 ${formatNumber(efficiencyGPerW, 1)} g/W 计算。`);
  } else if (maxPowerW !== null && bare !== null && bare > 0) {
    hoverPowerW = maxPowerW * bare ** -1.5;
    notes.push('悬停功率按「功率与推力的 1.5 次方成正比」从满油门功率折算。');
  } else if (efficiencyGPerW === null) {
    for (const motor of motors) {
      if (fieldNumber(motor.component, 'efficiency_g_per_w') === null) {
        pushMissing(missing, motor, 'efficiency_g_per_w');
      }
    }
    if (motors.length > 0) notes.push('缺少电机效率，且无法从满油门功率折算悬停功率。');
  }

  const hoverCurrentA =
    hoverPowerW !== null && voltageV !== null && voltageV > 0 ? hoverPowerW / voltageV : null;

  const value: PowerSummary | null =
    voltageV === null
      ? null
      : {
          voltageV,
          maxCurrentA,
          maxPowerW,
          hoverCurrentA,
          hoverPowerW,
          currentBasis,
          efficiencyGPerW,
        };

  return { value, missing, notes };
}

/* ---------------------------- 续航 ---------------------------- */

function computeEndurance(
  named: NamedComponent[],
  power: Metric<PowerSummary>,
): Metric<EnduranceSummary> {
  const missing: MissingRef[] = [];
  const notes: string[] = [];

  const batteries = named.filter((item) => item.spec.kind === 'battery');
  let capacityAh = 0;
  let capacityFound = false;

  for (const battery of batteries) {
    const capacityMah = fieldNumber(battery.component, 'capacity_mah');
    if (capacityMah === null) {
      pushMissing(missing, battery, 'capacity_mah');
      continue;
    }
    capacityFound = true;
    capacityAh += (capacityMah / 1000) * battery.qty;
  }

  const hoverCurrentA = power.value?.hoverCurrentA ?? null;
  if (hoverCurrentA === null) {
    notes.push('缺少悬停电流（需要悬停功率与电池电压），无法估算续航。');
  }

  if (!capacityFound || hoverCurrentA === null || hoverCurrentA <= 0) {
    return { value: null, missing, notes };
  }

  const minutes = ((capacityAh * USABLE_BATTERY_RATIO) / hoverCurrentA) * 60;
  notes.push(`按电池可用放电 ${formatNumber(USABLE_BATTERY_RATIO * 100, 0)}% 估算，实际随飞行方式与电池状态浮动。`);

  return {
    value: { minutes, capacityAh, usableRatio: USABLE_BATTERY_RATIO },
    missing,
    notes,
  };
}

/* ---------------------------- 成本 ---------------------------- */

function computeCost(named: NamedComponent[], mass: Metric<MassSummary>): Metric<CostSummary> {
  const missing: MissingRef[] = [];
  const notes: string[] = [];
  const byGroup = new Map<GroupId, number>();
  const lines: { label: string; amount: number; qty: number }[] = [];
  let total = 0;
  let contributors = 0;

  for (const item of named) {
    const price = fieldNumber(item.component, 'price');
    if (price === null) {
      pushMissing(missing, item, 'price');
      continue;
    }
    const amount = price * item.qty;
    total += amount;
    contributors += 1;
    lines.push({ label: item.name, amount, qty: item.qty });
    byGroup.set(item.spec.group, (byGroup.get(item.spec.group) ?? 0) + amount);
  }

  if (contributors === 0) {
    return { value: null, missing, notes };
  }

  if (missing.length > 0) {
    notes.push('部分部件未填价格，总价为已填部分之和。');
  }

  const massG = mass.value?.totalG ?? null;

  return {
    value: {
      total,
      per100g: massG !== null && massG > 0 ? total / (massG / 100) : null,
      byGroup: GROUP_SPECS.map((group) => ({
        group: group.id,
        label: group.label,
        amount: byGroup.get(group.id) ?? 0,
        share: total > 0 ? (byGroup.get(group.id) ?? 0) / total : 0,
      })).filter((entry) => entry.amount > 0),
      lines,
    },
    missing,
    notes,
  };
}

/* ---------------------------- 裕度校验 ---------------------------- */

interface MarginInput {
  twr: Metric<TwrSummary>;
  hover: Metric<HoverSummary>;
  power: Metric<PowerSummary>;
}

function computeMargins(named: NamedComponent[], input: MarginInput): MarginCheck[] {
  const checks: MarginCheck[] = [];

  // 1. 动力组数量一致性
  const driveTrain = named.filter((item) => item.spec.driveTrain);
  if (driveTrain.length > 0) {
    const counts = new Set(driveTrain.map((item) => item.qty));
    if (counts.size > 1) {
      checks.push({
        id: 'drivetrain-count',
        label: '动力组数量一致性',
        status: 'warn',
        detail: `电机/电调/桨的数量不一致（${driveTrain
          .map((item) => `${item.name} ${item.qty}`)
          .join('、')}），请确认是否为同轴双桨等特殊布局。`,
      });
    } else {
      checks.push({
        id: 'drivetrain-count',
        label: '动力组数量一致性',
        status: 'ok',
        detail: `电机 / 电调 / 桨 均为 ${formatNumber(driveTrain[0]?.qty ?? 0, 0)} 个。`,
      });
    }
  }

  // 2. 电机满油门电流 vs 电调持续电流
  const motorCurrents = named
    .filter((item) => item.spec.kind === 'motor')
    .map((item) => ({ name: item.name, value: fieldNumber(item.component, 'max_current_a') }))
    .filter((item): item is { name: string; value: number } => item.value !== null);
  const escCurrents = named
    .filter((item) => item.spec.kind === 'esc')
    .map((item) => ({ name: item.name, value: fieldNumber(item.component, 'cont_current_a') }))
    .filter((item): item is { name: string; value: number } => item.value !== null);

  const motorCurrent = motorCurrents[0]?.value ?? null;
  const escCurrent = escCurrents[0]?.value ?? null;

  if (motorCurrent === null || escCurrent === null) {
    checks.push({
      id: 'esc-current',
      label: '电调电流裕度',
      status: 'unknown',
      detail: '缺少电机最大电流或电调持续电流，无法校验。',
    });
  } else if (motorCurrent > escCurrent) {
    checks.push({
      id: 'esc-current',
      label: '电调电流裕度',
      status: 'fail',
      detail: `单电机最大电流 ${formatNumber(motorCurrent, 1)} A 已超过电调持续电流 ${formatNumber(escCurrent, 1)} A。`,
    });
  } else {
    const margin = escCurrent > 0 ? (1 - motorCurrent / escCurrent) * 100 : 0;
    checks.push({
      id: 'esc-current',
      label: '电调电流裕度',
      status: margin >= 20 ? 'ok' : 'warn',
      detail: `单电机最大电流 ${formatNumber(motorCurrent, 1)} A / 电调持续 ${formatNumber(escCurrent, 1)} A，余量 ${formatNumber(margin, 0)}%。`,
    });
  }

  // 3. 满油门总电流 vs 电池最大放电电流
  const batteries = named.filter((item) => item.spec.kind === 'battery');
  let batteryMaxCurrent: number | null = null;

  for (const battery of batteries) {
    const capacityMah = fieldNumber(battery.component, 'capacity_mah');
    const dischargeC = fieldNumber(battery.component, 'discharge_c');
    if (capacityMah === null || dischargeC === null) continue;
    batteryMaxCurrent = (batteryMaxCurrent ?? 0) + (capacityMah / 1000) * dischargeC * battery.qty;
  }

  const maxCurrentA = input.power.value?.maxCurrentA ?? null;

  if (maxCurrentA === null || batteryMaxCurrent === null) {
    checks.push({
      id: 'battery-current',
      label: '电池放电裕度',
      status: 'unknown',
      detail: '缺少满油门总电流或电池容量 / 放电倍率，无法校验。',
    });
  } else if (maxCurrentA > batteryMaxCurrent) {
    checks.push({
      id: 'battery-current',
      label: '电池放电裕度',
      status: 'fail',
      detail: `满油门总电流 ${formatNumber(maxCurrentA, 1)} A 超过电池最大放电 ${formatNumber(batteryMaxCurrent, 1)} A。`,
    });
  } else {
    const margin = batteryMaxCurrent > 0 ? (1 - maxCurrentA / batteryMaxCurrent) * 100 : 0;
    checks.push({
      id: 'battery-current',
      label: '电池放电裕度',
      status: margin >= 15 ? 'ok' : 'warn',
      detail: `满油门总电流 ${formatNumber(maxCurrentA, 1)} A / 电池最大放电 ${formatNumber(batteryMaxCurrent, 1)} A，余量 ${formatNumber(margin, 0)}%。`,
    });
  }

  // 4. 悬停电流 vs 电池最大放电电流
  const hoverCurrentA = input.power.value?.hoverCurrentA ?? null;
  if (hoverCurrentA === null || batteryMaxCurrent === null) {
    checks.push({
      id: 'hover-current',
      label: '悬停电流占电池能力',
      status: 'unknown',
      detail: '缺少悬停电流或电池放电能力，无法校验。',
    });
  } else {
    const ratio = batteryMaxCurrent > 0 ? hoverCurrentA / batteryMaxCurrent : 1;
    checks.push({
      id: 'hover-current',
      label: '悬停电流占电池能力',
      status: ratio <= 0.6 ? 'ok' : ratio <= 0.8 ? 'warn' : 'fail',
      detail: `悬停电流 ${formatNumber(hoverCurrentA, 1)} A，占电池最大放电的 ${formatNumber(ratio * 100, 0)}%。`,
    });
  }

  // 5. 悬停油门与安全系数预留
  const hover = input.hover.value;
  if (!hover) {
    checks.push({
      id: 'hover-throttle',
      label: '悬停油门预留',
      status: 'unknown',
      detail: '缺少起飞质量或总推力，无法校验悬停余量。',
    });
  } else if (!hover.feasible) {
    checks.push({
      id: 'hover-throttle',
      label: '悬停油门预留',
      status: 'fail',
      detail: `悬停油门 ${formatNumber(hover.throttlePct, 0)}% 超过预留上限 ${formatNumber(hover.reserveCapPct, 0)}%。`,
    });
  } else {
    checks.push({
      id: 'hover-throttle',
      label: '悬停油门预留',
      status: hover.headroomPct >= 10 ? 'ok' : 'warn',
      detail: `悬停油门 ${formatNumber(hover.throttlePct, 0)}%，预留上限 ${formatNumber(hover.reserveCapPct, 0)}%，余量 ${formatNumber(hover.headroomPct, 0)}%。`,
    });
  }

  return checks;
}
